import React, { useRef } from "react";
import { FormGroup, Label, FormFeedback } from "reactstrap";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";

interface GoogleMapPickerProps {
  value: {
    name?: string;
    latitude?: number;
    longitude?: number;
    formattedAddress?: string;
  };
  onChange: (location: {
    name?: string;
    latitude?: number;
    longitude?: number;
    formattedAddress?: string;
    addressLine1?: string;
    addressLine2?: string;
    country?: string;
    state?: string;
    city?: string;
    zipCode?: string;
  }) => void;
  onBlur?: () => void;
  invalid?: boolean;
  error?: string;
}

const libraries: ("places")[] = ["places"];

const GoogleMapPicker: React.FC<GoogleMapPickerProps> = ({
  value,
  onChange,
  onBlur,
  invalid = false,
  error
}) => {
  const acRef = useRef<google.maps.places.Autocomplete | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-maps-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string,
    libraries
  });

  const handlePlaceChanged = () => {
    const place = acRef.current?.getPlace();
    if (!place || !place.geometry || !place.geometry.location) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const name = place.name || "";
    const formattedAddress = place.formatted_address || "";

    const get = (type: string) =>
      place.address_components?.find(c => c.types.includes(type))?.long_name || "";

    onChange({
      name,
      latitude: lat,
      longitude: lng,
      formattedAddress,
      addressLine1: [get("street_number"), get("route")].filter(Boolean).join(" ").trim(),
      addressLine2: get("subpremise"),
      city: get("locality") || get("administrative_area_level_2"),
      state: get("administrative_area_level_1"),
      country: get("country"),
      zipCode: get("postal_code")
    });
  };

  if (!isLoaded) {
    return (
      <FormGroup>
        <Label className="form-label">Location</Label>
        <div className="form-control" style={{ height: 45, display: "flex", alignItems: "center" }}>
          Loading Google…
        </div>
      </FormGroup>
    );
  }

  return (
    <FormGroup>
      <Label className="form-label">Location</Label>
      <Autocomplete
        onLoad={(instance) => (acRef.current = instance)}
        onPlaceChanged={handlePlaceChanged}
      >
        <input
          type="text"
          placeholder="Search for a location or business…"
          className={`form-control${invalid ? " is-invalid" : ""}`}
          onBlur={onBlur}
          style={{
            width: "100%",
            height: "45px",
            padding: "12px 16px",
            fontSize: "14px",
            border: "1px solid #dee2e6",
            borderRadius: "8px",
            backgroundColor: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            transition: "all 0.2s ease-in-out",
            marginBottom: "0.5rem"
          }}
        />
      </Autocomplete>

      {error && <FormFeedback type="invalid">{error}</FormFeedback>}
    </FormGroup>
  );
};

export default GoogleMapPicker;
